export const LIVEAVATAR_START_PAYLOAD_ERROR_CODE =
  "LIVEAVATAR_START_PAYLOAD_INVALID";
export const LIVEAVATAR_TOKEN_EMPTY_RESPONSE_ERROR_CODE =
  "LIVEAVATAR_TOKEN_EMPTY_RESPONSE";
export const LIVEAVATAR_TOKEN_REQUEST_ERROR_CODE =
  "LIVEAVATAR_TOKEN_REQUEST_ERROR";
export const LIVEAVATAR_MICROPHONE_PERMISSION_ERROR_CODE =
  "LIVEAVATAR_MICROPHONE_PERMISSION_REQUIRED";
export const LIVEAVATAR_SESSION_IDLE_TIMEOUT_ERROR_CODE =
  "LIVEAVATAR_SESSION_IDLE_TIMEOUT";
export const LIVEAVATAR_SESSION_MAX_DURATION_ERROR_CODE =
  "LIVEAVATAR_SESSION_MAX_DURATION";
export const LIVEAVATAR_SESSION_ENDED_ERROR_CODE = "LIVEAVATAR_SESSION_ENDED";

/** Default keep-alive cadence — under LiveAvatar’s ~5 min idle ceiling (see /sessions/keep-alive). */
export const LIVEAVATAR_KEEP_ALIVE_INTERVAL_MS = 90 * 1000;

export const LIVEAVATAR_UNEXPECTED_SESSION_END_REASONS = new Set([
  "IDLE_TIMEOUT",
  "MAX_DURATION_REACHED",
  "ZOMBIE_SESSION_REAP",
  "SERVER_ERROR",
  "NO_CREDITS",
  "AVATAR_DELETED",
]);

export const parseLiveAvatarSessionEndReason = (eventData) => {
  const raw =
    eventData?.end_reason ??
    eventData?.endReason ??
    eventData?.reason ??
    null;
  return typeof raw === "string" && raw.trim() ? raw.trim() : null;
};

export const isUnexpectedLiveAvatarSessionEndReason = (endReason) =>
  Boolean(endReason) &&
  LIVEAVATAR_UNEXPECTED_SESSION_END_REASONS.has(endReason);

export const liveAvatarSessionEndErrorCode = (endReason) => {
  if (endReason === "IDLE_TIMEOUT") {
    return LIVEAVATAR_SESSION_IDLE_TIMEOUT_ERROR_CODE;
  }
  if (endReason === "MAX_DURATION_REACHED") {
    return LIVEAVATAR_SESSION_MAX_DURATION_ERROR_CODE;
  }
  return LIVEAVATAR_SESSION_ENDED_ERROR_CODE;
};

export const resolveLiveAvatarSessionApiPath = (apiUrl, pathSegment) => {
  const base = String(apiUrl || "").replace(/\/+$/, "");
  return `${base}/sessions/${pathSegment}`;
};

export const postLiveAvatarSessionRequest = async ({
  apiUrl,
  sessionToken,
  sessionId,
  pathSegment,
  body = {},
}) => {
  const url = resolveLiveAvatarSessionApiPath(apiUrl, pathSegment);
  const response = await fetch(url, {
    method: "POST",
    headers: {
      Authorization: `Bearer ${sessionToken}`,
      Accept: "application/json",
      "Content-Type": "application/json",
    },
    body: JSON.stringify({
      ...(sessionId ? {session_id: sessionId} : {}),
      ...body,
    }),
  });
  return response;
};

/** В prod: `localStorage.setItem("LIVEAVATAR_STREAM_LOG", "1")` и перезагрузка. */
export const logLiveAvatarStream = (message, detail) => {
  if (typeof window === "undefined") return;
  const enabled =
    Boolean(import.meta.env?.DEV) ||
    (typeof localStorage !== "undefined" &&
      localStorage.getItem("LIVEAVATAR_STREAM_LOG") === "1");
  if (!enabled) return;
  const ts = new Date().toISOString();
  if (detail !== undefined) {
    console.log(`[LiveAvatar][stream ${ts}]`, message, detail);
  } else {
    console.log(`[LiveAvatar][stream ${ts}]`, message);
  }
};

export class LiveAvatarStartPayloadError extends Error {
  constructor(message, details = {}) {
    super(message);
    this.name = "LiveAvatarStartPayloadError";
    this.code = LIVEAVATAR_START_PAYLOAD_ERROR_CODE;
    this.details = details;
  }
}

export class LiveAvatarTokenError extends Error {
  constructor(code, message, details = {}) {
    super(message);
    this.name = "LiveAvatarTokenError";
    this.code = code;
    this.details = details;
  }
}

const pickFirstString = (values) => {
  for (const value of values) {
    if (typeof value === "string" && value.trim() !== "") {
      return value;
    }
  }

  return null;
};

export const normalizeLiveAvatarStartResponse = (rawStartResponse) => {
  const startData = rawStartResponse?.data || rawStartResponse || {};

  const livekitUrl = pickFirstString([
    startData?.livekit_url,
    startData?.livekitUrl,
    startData?.livekit_server_url,
    startData?.livekitServerUrl,
    startData?.livekit_ws_url,
    startData?.livekitWsUrl,
    startData?.room_url,
    startData?.roomUrl,
    startData?.rtc_url,
    startData?.rtcUrl,
    startData?.url,
    startData?.livekit?.url,
    startData?.livekit?.server_url,
    startData?.livekit?.serverUrl,
    startData?.livekit_client?.url,
    startData?.livekitClient?.url,
  ]);

  const livekitToken = pickFirstString([
    startData?.livekit_token,
    startData?.livekitToken,
    startData?.livekit_client_token,
    startData?.livekitClientToken,
    startData?.room_token,
    startData?.roomToken,
    startData?.rtc_token,
    startData?.rtcToken,
    startData?.access_token,
    startData?.accessToken,
    startData?.token,
    startData?.livekit?.token,
    startData?.livekit?.client_token,
    startData?.livekit?.clientToken,
    startData?.livekit_client?.token,
    startData?.livekitClient?.token,
  ]);

  if (!livekitUrl || !livekitToken) {
    throw new LiveAvatarStartPayloadError(
      "LiveAvatar start response does not contain required LiveKit data",
      {
        availableKeys: Object.keys(startData || {}),
        hasLivekitUrl: Boolean(livekitUrl),
        hasLivekitToken: Boolean(livekitToken),
      },
    );
  }

  return {
    startData,
    livekitUrl,
    livekitToken,
    sessionId: pickFirstString([
      startData?.session_id,
      startData?.sessionId,
      startData?.session?.id,
      startData?.session?.session_id,
      startData?.data?.session_id,
      startData?.data?.sessionId,
      rawStartResponse?.session_id,
      rawStartResponse?.sessionId,
    ]),
  };
};
